import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import {
  ArrowLeft, Wind, TrendingUp, TrendingDown, AlertTriangle, Trash2, Loader2,
  Edit2, Save, X, UserPlus, Search,
} from 'lucide-react';
import { toast } from 'sonner';

const TEST_LABELS = { YOYO: 'Yo-Yo Test', GACON: 'Gacon Test' };

export default function AerobicSessionDetailPage() {
  const { sessionId } = useParams();
  const { api, user } = useAuth();
  const { language } = useLanguage();
  const navigate = useNavigate();
  const t = (en, it) => (language === 'IT' ? it : en);

  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);

  // Edit-mode drafts (never mutate `session` directly)
  const [metaDraft, setMetaDraft] = useState({ date: '', notes: '' });
  const [rowsDraft, setRowsDraft] = useState([]); // clone of session.results with edits

  // Add-athlete dialog state
  const [showAddAthlete, setShowAddAthlete] = useState(false);
  const [teamAthletes, setTeamAthletes] = useState([]);
  const [athleteSearch, setAthleteSearch] = useState('');

  const canEdit = user?.role === 'ADMIN' || user?.role === 'SNC';

  const fetchSession = useCallback(async () => {
    try {
      const r = await api.get(`/aerobic/sessions/${sessionId}`);
      setSession(r.data);
    } catch (err) {
      navigate('/staff/aerobic');
    } finally {
      setLoading(false);
    }
  }, [api, sessionId, navigate]);

  useEffect(() => { fetchSession(); }, [fetchSession]);

  const enterEdit = () => {
    if (!session) return;
    setMetaDraft({ date: session.date || '', notes: session.notes || '' });
    setRowsDraft((session.results || []).map((r) => ({ ...r, __pendingDelete: false })));
    setIsEditing(true);
  };

  const cancelEdit = () => {
    setIsEditing(false);
    setMetaDraft({ date: '', notes: '' });
    setRowsDraft([]);
    setShowAddAthlete(false);
  };

  const handleDelete = async () => {
    if (!window.confirm(t('Delete this entire test?', 'Eliminare l\'intero test?'))) return;
    try {
      await api.delete(`/aerobic/sessions/${sessionId}`);
      toast.success(t('Test deleted', 'Test eliminato'));
      navigate('/staff/aerobic');
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  const updateRowDraft = (id, patch) => {
    setRowsDraft((prev) => prev.map((r) => (r.id === id ? { ...r, ...patch } : r)));
  };

  const openAddAthlete = async () => {
    if (!session?.team_id) {
      toast.error(t('Session has no team', 'Nessuna squadra nel test'));
      return;
    }
    setShowAddAthlete(true);
    setAthleteSearch('');
    try {
      const res = await api.get(`/athletes?team_id=${session.team_id}`);
      const existingIds = new Set(rowsDraft.filter((r) => !r.__pendingDelete).map((r) => r.athlete_id));
      setTeamAthletes((Array.isArray(res.data) ? res.data : []).filter((a) => !existingIds.has(a.id)));
    } catch (e) {
      setTeamAthletes([]);
    }
  };

  const addAthleteRow = (athlete) => {
    // Append a draft placeholder row — will be POSTed on save (empty numeric fields until user types)
    setRowsDraft((prev) => [
      ...prev,
      {
        id: `__new_${athlete.id}_${Date.now()}`,
        __new: true,
        athlete_id: athlete.id,
        athlete_name: `${athlete.first_name} ${athlete.last_name}`,
        final_speed_kmh: '',
        total_distance_m: '',
        mas_vam_kmh: '',
        level_reached: '',
        heart_rate_bpm: '',
      },
    ]);
    setTeamAthletes((prev) => prev.filter((a) => a.id !== athlete.id));
  };

  const markRowDelete = (id) => {
    setRowsDraft((prev) => prev.map((r) => (r.id === id ? { ...r, __pendingDelete: true } : r)));
  };
  const undoRowDelete = (id) => {
    setRowsDraft((prev) => prev.map((r) => (r.id === id ? { ...r, __pendingDelete: false } : r)));
  };

  const _num = (v) => {
    if (v === '' || v == null) return null;
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  };

  const handleSave = async () => {
    if (saving) return;
    setSaving(true);
    try {
      // 1) Session metadata patch (only if changed)
      const metaPatch = {};
      if (metaDraft.date && metaDraft.date !== session.date) metaPatch.date = metaDraft.date;
      if ((metaDraft.notes || '') !== (session.notes || '')) metaPatch.notes = metaDraft.notes || '';
      if (Object.keys(metaPatch).length > 0) {
        await api.put(`/aerobic/sessions/${sessionId}`, metaPatch);
      }

      // 2) Row deletes
      const toDelete = rowsDraft.filter((r) => r.__pendingDelete && !r.__new);
      for (const r of toDelete) {
        await api.delete(`/aerobic/results/${r.id}`);
      }

      // 3) New rows → POST /results (validation: MAS + speed + distance required)
      const toAdd = rowsDraft.filter((r) => r.__new && !r.__pendingDelete);
      for (const r of toAdd) {
        const mas = _num(r.mas_vam_kmh);
        const speed = _num(r.final_speed_kmh);
        const distance = _num(r.total_distance_m);
        if (!mas || !speed || !distance) {
          throw new Error(t(
            `Athlete "${r.athlete_name}" needs MAS/VAM, Speed and Distance`,
            `L'atleta "${r.athlete_name}" richiede MAS/VAM, Velocità e Distanza`,
          ));
        }
        await api.post(`/aerobic/sessions/${sessionId}/results`, {
          athlete_id: r.athlete_id,
          final_speed_kmh: speed,
          total_distance_m: distance,
          mas_vam_kmh: mas,
          level_reached: r.level_reached || null,
          heart_rate_bpm: _num(r.heart_rate_bpm),
        });
      }

      // 4) Existing rows → PUT /results/{id} with only changed numeric fields
      const original = new Map((session.results || []).map((r) => [r.id, r]));
      const toEdit = rowsDraft.filter((r) => !r.__new && !r.__pendingDelete);
      for (const r of toEdit) {
        const orig = original.get(r.id);
        if (!orig) continue;
        const patch = {};
        const fields = ['final_speed_kmh', 'total_distance_m', 'mas_vam_kmh',
                        'level_reached', 'heart_rate_bpm'];
        for (const f of fields) {
          const orig_v = orig[f] ?? null;
          const new_v = ['level_reached'].includes(f)
            ? (r[f] || null)
            : _num(r[f]);
          if (new_v !== orig_v && new_v != null) patch[f] = new_v;
        }
        if (Object.keys(patch).length > 0) {
          await api.put(`/aerobic/results/${r.id}`, patch);
        }
      }

      toast.success(t('Test updated', 'Test aggiornato'));
      setIsEditing(false);
      await fetchSession();
    } catch (err) {
      toast.error(err?.message || err?.response?.data?.detail || t('Save failed', 'Salvataggio fallito'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="flex justify-center py-24"><Loader2 className="w-6 h-6 animate-spin text-zinc-500" /></div>;
  if (!session) return null;

  const s = session.summary;
  const displayRows = (isEditing ? rowsDraft : session.results || []).slice()
    .sort((a, b) => (Number(b.mas_vam_kmh) || 0) - (Number(a.mas_vam_kmh) || 0));

  return (
    <div className="space-y-6 fade-in" data-testid="aerobic-session-detail">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-4 min-w-0">
          <Button variant="ghost" size="icon" onClick={() => navigate('/staff/aerobic')} className="text-zinc-400 hover:text-zinc-100">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="min-w-0">
            <h1 className="text-xl font-bold text-zinc-100 flex items-center gap-2 flex-wrap">
              <Wind className="w-5 h-5 text-teal-400" /> {TEST_LABELS[session.test_type]}
              {session.protocol_name && <span className="text-teal-400 text-sm font-normal">({session.protocol_name})</span>}
            </h1>
            <p className="text-sm text-zinc-500">
              {session.date} • {session.team_code || ''} • {displayRows.filter((r) => !r.__pendingDelete).length} {t('results', 'risultati')}
              {session.updated_at && (
                <span className="ml-2 text-[10px] text-zinc-600">
                  {t('Last edited', 'Ultima modifica')} {String(session.updated_at).substring(0, 10)}
                </span>
              )}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {canEdit && !isEditing && (
            <Button variant="ghost" size="sm" onClick={enterEdit} className="text-teal-400 hover:text-teal-300 min-h-[44px]" data-testid="edit-btn">
              <Edit2 className="w-4 h-4 mr-1" /> {t('Edit', 'Modifica')}
            </Button>
          )}
          {canEdit && isEditing && (
            <>
              <Button variant="ghost" size="sm" onClick={cancelEdit} disabled={saving} className="text-zinc-400 hover:text-zinc-100 min-h-[44px]" data-testid="cancel-edit-btn">
                <X className="w-4 h-4 mr-1" /> {t('Cancel', 'Annulla')}
              </Button>
              <Button size="sm" onClick={handleSave} disabled={saving} className="bg-teal-500 text-zinc-950 hover:bg-teal-400 min-h-[44px]" data-testid="save-edit-btn">
                <Save className="w-4 h-4 mr-1" /> {saving ? t('Saving...', 'Salvataggio...') : t('Save', 'Salva')}
              </Button>
            </>
          )}
          {canEdit && !isEditing && (
            <Button variant="ghost" size="sm" onClick={handleDelete} className="text-red-400 hover:text-red-300 min-h-[44px]" data-testid="delete-btn">
              <Trash2 className="w-4 h-4 mr-1" /> {t('Delete', 'Elimina')}
            </Button>
          )}
        </div>
      </div>

      {/* Metadata */}
      {isEditing ? (
        <Card className="bg-zinc-900/50 border-zinc-800" data-testid="metadata-editor">
          <CardContent className="p-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs text-zinc-400">{t('Date', 'Data')}</Label>
              <Input type="date" value={metaDraft.date} onChange={(e) => setMetaDraft({ ...metaDraft, date: e.target.value })}
                className="bg-zinc-950 border-zinc-800 mt-1" data-testid="meta-date-input" />
            </div>
            <div className="sm:col-span-2">
              <Label className="text-xs text-zinc-400">{t('Notes', 'Note')}</Label>
              <Textarea value={metaDraft.notes} onChange={(e) => setMetaDraft({ ...metaDraft, notes: e.target.value })}
                className="bg-zinc-950 border-zinc-800 mt-1 min-h-[60px]" data-testid="meta-notes-input" />
            </div>
          </CardContent>
        </Card>
      ) : (
        session.notes && <p className="text-sm text-zinc-400 italic">{session.notes}</p>
      )}

      {/* Stats */}
      {s && !isEditing && (
        <div className="grid grid-cols-3 gap-4">
          {s.team_avg_mas != null && (
            <Card className="bg-zinc-900/50 border-zinc-800" data-testid="stat-avg-mas"><CardContent className="p-4 text-center">
              <div className="text-xl font-bold text-teal-400">{s.team_avg_mas} km/h</div>
              <div className="text-xs text-zinc-500">{t('Team Avg MAS', 'MAS Media')}</div>
            </CardContent></Card>
          )}
          {s.best_mas != null && (
            <Card className="bg-emerald-500/10 border-emerald-500/30" data-testid="stat-best-mas"><CardContent className="p-4 text-center">
              <TrendingUp className="w-4 h-4 text-emerald-400 mx-auto mb-1" />
              <div className="text-xl font-bold text-emerald-400">{s.best_mas} km/h</div>
              <div className="text-xs text-zinc-500">{t('Best MAS', 'Migliore')}</div>
            </CardContent></Card>
          )}
          {s.lowest_mas != null && (
            <Card className="bg-red-500/10 border-red-500/30" data-testid="stat-lowest-mas"><CardContent className="p-4 text-center">
              <TrendingDown className="w-4 h-4 text-red-400 mx-auto mb-1" />
              <div className="text-xl font-bold text-red-400">{s.lowest_mas} km/h</div>
              <div className="text-xs text-zinc-500">{t('Lowest MAS', 'Minore')}</div>
            </CardContent></Card>
          )}
        </div>
      )}

      {session.alerts?.length > 0 && !isEditing && (
        <Card className="bg-red-500/5 border-red-500/30">
          <CardHeader className="pb-2"><CardTitle className="text-red-400 text-sm flex items-center gap-2">
            <AlertTriangle className="w-4 h-4" /> {t('Alerts', 'Allerte')}
          </CardTitle></CardHeader>
          <CardContent className="space-y-1">
            {session.alerts.map((a, i) => (
              <div key={i} className="text-sm flex justify-between p-2 bg-red-500/5 rounded-sm">
                <span className="text-zinc-300">{a.athlete_name}</span>
                <span className="text-red-400 text-xs">
                  {a.type === 'PERFORMANCE_DROP' && `MAS drop ${a.drop_pct}%`}
                  {a.type === 'BELOW_TEAM_AVG' && `${a.below_pct}% below avg`}
                </span>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Results table */}
      <Card className="bg-zinc-900/50 border-zinc-800">
        <CardHeader className="pb-2 flex flex-row items-center justify-between">
          <CardTitle className="text-zinc-100 text-base">{t('Results', 'Risultati')}</CardTitle>
          {isEditing && (
            <Button size="sm" onClick={openAddAthlete} className="bg-teal-500 text-zinc-950 hover:bg-teal-400 min-h-[44px]" data-testid="add-athlete-btn">
              <UserPlus className="w-4 h-4 mr-1" /> {t('Add athlete', 'Aggiungi atleta')}
            </Button>
          )}
        </CardHeader>
        <CardContent className="overflow-x-auto">
          <table className="w-full min-w-[560px]">
            <thead><tr className="border-b border-zinc-800">
              <th className="text-left p-2 text-xs text-zinc-500">{t('Athlete', 'Atleta')}</th>
              <th className="text-center p-2 text-xs text-zinc-500">MAS/VAM</th>
              <th className="text-center p-2 text-xs text-zinc-500">{t('Speed', 'Vel.')}</th>
              <th className="text-center p-2 text-xs text-zinc-500">{t('Distance', 'Dist.')}</th>
              {session.test_type === 'YOYO' && <th className="text-center p-2 text-xs text-zinc-500">{t('Level', 'Livello')}</th>}
              <th className="text-center p-2 text-xs text-zinc-500">HR</th>
              {isEditing && <th className="text-right p-2 text-xs text-zinc-500 w-10"></th>}
            </tr></thead>
            <tbody>
              {displayRows.map((r) => {
                const isDel = r.__pendingDelete;
                return (
                  <tr key={r.id} className={`border-b border-zinc-800/50 hover:bg-zinc-800/20 ${isDel ? 'opacity-40 line-through' : ''}`}
                      data-testid={`result-row-${r.athlete_id}`}>
                    <td className="p-2">
                      {isEditing ? (
                        <span className="text-sm font-medium text-zinc-200">{r.athlete_name}</span>
                      ) : (
                        <Link to={`/staff/aerobic/athlete/${r.athlete_id}`} className="text-sm font-medium text-zinc-200 hover:text-teal-400"
                          data-testid={`athlete-link-${r.athlete_id}`}>{r.athlete_name}</Link>
                      )}
                    </td>
                    <td className="text-center p-1">
                      {isEditing && !isDel ? (
                        <Input type="number" step="0.1" value={r.mas_vam_kmh ?? ''} onChange={(e) => updateRowDraft(r.id, { mas_vam_kmh: e.target.value })}
                          className="bg-zinc-950 border-zinc-800 h-9 w-20 text-sm text-center" data-testid={`edit-mas-${r.athlete_id}`} />
                      ) : (
                        <span className="text-teal-400 font-semibold text-sm">{r.mas_vam_kmh} km/h</span>
                      )}
                    </td>
                    <td className="text-center p-1">
                      {isEditing && !isDel ? (
                        <Input type="number" step="0.1" value={r.final_speed_kmh ?? ''} onChange={(e) => updateRowDraft(r.id, { final_speed_kmh: e.target.value })}
                          className="bg-zinc-950 border-zinc-800 h-9 w-20 text-sm text-center" data-testid={`edit-speed-${r.athlete_id}`} />
                      ) : (
                        <span className="text-zinc-300 text-sm">{r.final_speed_kmh}</span>
                      )}
                    </td>
                    <td className="text-center p-1">
                      {isEditing && !isDel ? (
                        <Input type="number" step="1" value={r.total_distance_m ?? ''} onChange={(e) => updateRowDraft(r.id, { total_distance_m: e.target.value })}
                          className="bg-zinc-950 border-zinc-800 h-9 w-24 text-sm text-center" data-testid={`edit-distance-${r.athlete_id}`} />
                      ) : (
                        <span className="text-zinc-300 text-sm">{r.total_distance_m}m</span>
                      )}
                    </td>
                    {session.test_type === 'YOYO' && (
                      <td className="text-center p-1">
                        {isEditing && !isDel ? (
                          <Input value={r.level_reached ?? ''} onChange={(e) => updateRowDraft(r.id, { level_reached: e.target.value })}
                            className="bg-zinc-950 border-zinc-800 h-9 w-16 text-sm text-center" data-testid={`edit-level-${r.athlete_id}`} />
                        ) : (
                          <span className="text-zinc-400 text-sm">{r.level_reached || '—'}</span>
                        )}
                      </td>
                    )}
                    <td className="text-center p-1">
                      {isEditing && !isDel ? (
                        <Input type="number" step="1" value={r.heart_rate_bpm ?? ''} onChange={(e) => updateRowDraft(r.id, { heart_rate_bpm: e.target.value })}
                          className="bg-zinc-950 border-zinc-800 h-9 w-20 text-sm text-center" data-testid={`edit-hr-${r.athlete_id}`} />
                      ) : (
                        <span className="text-zinc-500 text-sm">{r.heart_rate_bpm || '—'}</span>
                      )}
                    </td>
                    {isEditing && (
                      <td className="text-right p-1">
                        {!isDel ? (
                          <button type="button" onClick={() => markRowDelete(r.id)} className="p-2 text-red-400 hover:text-red-300 min-w-[44px] min-h-[44px]"
                            data-testid={`remove-athlete-${r.athlete_id}`} title={t('Remove athlete', 'Rimuovi atleta')}>
                            <Trash2 className="w-4 h-4" />
                          </button>
                        ) : (
                          <button type="button" onClick={() => undoRowDelete(r.id)} className="text-xs text-teal-400 hover:text-teal-300 min-h-[44px] px-2"
                            data-testid={`undo-remove-${r.athlete_id}`}>
                            {t('Undo', 'Annulla')}
                          </button>
                        )}
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {/* Add-athlete overlay */}
      {isEditing && showAddAthlete && (
        <Card className="fixed bottom-4 left-4 right-4 sm:left-auto sm:right-4 sm:w-96 z-40 bg-zinc-900 border-teal-500/40 shadow-2xl" data-testid="add-athlete-picker">
          <CardHeader className="pb-2 flex flex-row items-center justify-between">
            <CardTitle className="text-zinc-100 text-sm">{t('Add athletes', 'Aggiungi atleti')}</CardTitle>
            <button onClick={() => setShowAddAthlete(false)} className="text-zinc-400 hover:text-zinc-100 p-1" data-testid="close-add-athlete">
              <X className="w-4 h-4" />
            </button>
          </CardHeader>
          <CardContent className="space-y-2">
            <div className="relative">
              <Search className="w-3.5 h-3.5 text-zinc-500 absolute top-1/2 left-2.5 -translate-y-1/2" />
              <Input value={athleteSearch} onChange={(e) => setAthleteSearch(e.target.value)}
                placeholder={t('Search athlete...', 'Cerca atleta...')}
                className="bg-zinc-950 border-zinc-800 h-9 pl-8 text-sm" data-testid="add-athlete-search" />
            </div>
            <div className="max-h-64 overflow-y-auto border border-zinc-800 rounded-sm divide-y divide-zinc-800">
              {teamAthletes.length === 0 && (
                <p className="p-3 text-xs text-zinc-500 italic">{t('No athletes available', 'Nessun atleta disponibile')}</p>
              )}
              {teamAthletes
                .filter((a) => {
                  const q = athleteSearch.toLowerCase().trim();
                  if (!q) return true;
                  return `${a.first_name} ${a.last_name}`.toLowerCase().includes(q);
                })
                .map((a) => (
                  <button
                    key={a.id}
                    type="button"
                    onClick={() => addAthleteRow(a)}
                    className="w-full text-left px-3 py-2 min-h-[44px] hover:bg-zinc-800 flex items-center justify-between gap-2"
                    data-testid={`add-picker-row-${a.id}`}
                  >
                    <span className="text-sm text-zinc-200">{a.first_name} {a.last_name}</span>
                    {a.position && <span className="text-[10px] px-1.5 py-0.5 bg-zinc-800 text-zinc-400 rounded">{a.position}</span>}
                  </button>
                ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
